import { EvidenceBlob } from '../engine/martech-scanner-constants';

export const FullScanStatus = {
  PENDING: 'pending',
  RUNNING: 'running',
  PARTIAL: 'partial',
  DONE: 'done',
  FAILED: 'failed'
} as const;
export type FullScanStatusType = (typeof FullScanStatus)[keyof typeof FullScanStatus];

// A job in any of these has stopped polling. DONE and PARTIAL both merge
// into the report (see DigitalAudit.tsx's merge effect) - PARTIAL just means
// the deadline/page-cap hit before every targeted page finished, not that
// the pages it did capture are unusable. Only FAILED merges nothing.
export const TERMINAL_FULL_SCAN_STATUSES: FullScanStatusType[] = [
  FullScanStatus.DONE,
  FullScanStatus.PARTIAL,
  FullScanStatus.FAILED
];

export interface FullScanProgress {
  pagesCompleted: number;
  pagesTotal: number;
}

export interface FullScanPageCapture {
  url: string;
  requestCount: number;
  firstPartyHosts: string[];
  thirdPartyHosts: string[];
  evidenceBlobs: EvidenceBlob[];
  requests: string[];
  // Subset of `requests` observed after a supplied consent-accept selector
  // was clicked (see full-scan-merge-utils.ts/tag-fire-utils.ts, which use
  // this to tag wire fires as post-consent instead of pre-consent).
  postConsentRequests: string[];
  // Redacted, size-capped snapshot of window.dataLayer, pre- and
  // post-consent (see engine/datalayer-inventory-utils.ts).
  dataLayerSnapshot: unknown[];
  postConsentDataLayerSnapshot: unknown[];
  errors: string[];
}

export interface FullScanResult {
  url: string;
  domain: string;
  scannedAt: string;
  pages: FullScanPageCapture[];
  scriptSrcs: string[];
  errors: string[];
}

export interface FullScanJobStatus {
  id: string;
  url: string;
  domain: string;
  status: FullScanStatusType;
  progress: FullScanProgress;
  result: FullScanResult | null;
  error: string | null;
}

// DEV-24211: a DONE/PARTIAL job can still carry a null or empty result (e.g.
// the crawl's result upload failed, or every page was blocked without being
// recorded as a page error) - awaitJSON never validates the response shape,
// so both the merge effect and the status banner check this before treating
// a terminal job as mergeable, rather than merging in nothing or throwing.
// A type guard (narrowing `result` to non-null) rather than a plain boolean,
// since the merge effect immediately destructures `job.result`.
export const hasUsableFullScanResult = (
  job: FullScanJobStatus | null | undefined
): job is FullScanJobStatus & { result: FullScanResult } =>
  Boolean(job?.result && Array.isArray(job.result.pages) && job.result.pages.length > 0);
